import { CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import {
  PieChartComponent,
  PieChartSegment,
} from "@shared/components/pie-chart/pie-chart.component";
import { PaymentMethodPipe } from "@shared/pipes/payment-method.pipe";
import { SalesChannelPipe } from "@shared/pipes/sales-channel.pipe";
import { SkeletonModule } from "primeng/skeleton";
import { TableModule } from "primeng/table";
import { catchError, map, of, shareReplay, switchMap } from "rxjs";
import { EventsManagementService } from "../../../../services/events-management.service";
import { getParams } from "../../../../../utils/utils";

type OverviewTab = "payment" | "channel";

interface SummaryRow {
  label: string;
  quantity: number;
  amount: number;
}

interface OverviewView {
  segments: PieChartSegment[];
  colors: string[];
  rows: SummaryRow[];
  totalQuantity: number;
  totalValue: number;
}

@Component({
  selector: "app-sales-overview-section",
  imports: [CommonModule, SkeletonModule, TableModule, PieChartComponent],
  templateUrl: "./sales-overview-section.component.html",
  styleUrl: "./sales-overview-section.component.scss",
})
export class SalesOverviewSectionComponent {
  private readonly _eventService = inject(EventsManagementService);
  private readonly _paymentMethodPipe = new PaymentMethodPipe();
  private readonly _salesChannelPipe = new SalesChannelPipe();

  activeTab: OverviewTab = "payment";

  // Paleta das fatias (segue o mockup).
  private readonly paymentColors = ["#7c0084", "#00c6e3", "#ffe600", "#828282"];
  private readonly channelColors: Record<string, string> = {
    online: "#5fa052",
    affiliate: "#00c6e3",
    pdv: "#8bc34a",
  };

  paymentView$ = getParams("id").pipe(
    switchMap((eventId) =>
      this._eventService
        .getOrdersByPaymentMethod(eventId)
        .pipe(catchError(() => of([]))),
    ),
    map((items) => {
      const rows = items.map((item) => ({
        label: this._paymentMethodPipe.transform(item.payment_method),
        quantity: item.sales_quantity,
        amount: item.total_amount,
      }));
      const segments = rows.map((row) => ({
        label: row.label,
        value: row.quantity,
        amount: row.amount,
      }));
      const colors = rows.map(
        (_row, index) => this.paymentColors[index % this.paymentColors.length],
      );
      return this.buildView(rows, segments, colors);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  channelView$ = getParams("id").pipe(
    switchMap((eventId) => this._eventService.getSalesByChannel(eventId)),
    map((items) => {
      const rows = items.map((item) => ({
        label: this._salesChannelPipe.transform(item.sales_channel),
        quantity: item.sales_quantity,
        amount: item.total_amount,
      }));
      const segments = items.map((item) => ({
        label: this.channelShortLabel(item.sales_channel),
        value: item.sales_quantity,
        amount: item.total_amount,
      }));
      const colors = items.map(
        (item) => this.channelColors[item.sales_channel] ?? "#828282",
      );
      return this.buildView(rows, segments, colors);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  setActiveTab(tab: OverviewTab): void {
    this.activeTab = tab;
  }

  private buildView(
    rows: SummaryRow[],
    segments: PieChartSegment[],
    colors: string[],
  ): OverviewView {
    return {
      rows,
      segments,
      colors,
      totalQuantity: rows.reduce((total, row) => total + row.quantity, 0),
      totalValue: rows.reduce((total, row) => total + row.amount, 0),
    };
  }

  private channelShortLabel(channel: string): string {
    switch (channel) {
      case "online":
        return "Web";
      case "affiliate":
        return "App Mobile";
      case "pdv":
        return "PDV Físico";
      default:
        return channel;
    }
  }
}
